import { useState, useRef } from 'react';
import { Upload, FileText, Trash2, AlertTriangle, Lock, Download, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../../../App';
import { Button } from '../../ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../../ui/alert-dialog';

const DocumentsTab = ({ documents, setDocuments, onPanicRoom, activeSubscription }) => {
  const [uploading, setUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  
  if (!activeSubscription?.can_upload) {
    return (
      <div className="text-center py-12">
        <Lock className="w-16 h-16 text-gray-600 mx-auto mb-4" />
        <p className="text-gray-400">Souscrivez et signez d'abord</p>
      </div>
    );
  }
  
  const processFile = async (file) => {
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/documents/upload`, formData, { 
        headers: { 'Content-Type': 'multipart/form-data', 'Authorization': `Bearer ${token}` }, 
        withCredentials: true 
      });
      toast.success('Document uploadé !');
      const res = await axios.get(`${API}/documents`, { headers: { 'Authorization': `Bearer ${token}` }, withCredentials: true });
      setDocuments(res.data.documents);
    } catch (error) { 
      toast.error(error.response?.data?.detail || 'Erreur'); 
    } finally { 
      setUploading(false); 
    }
  };
  
  const handleUpload = (e) => {
    const file = e.target.files?.[0];
    processFile(file);
  };
  
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) processFile(file);
  };
  
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };
  
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };
  
  const handleDelete = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/documents/${id}`, { headers: { 'Authorization': `Bearer ${token}` }, withCredentials: true });
      setDocuments(documents.filter(d => d.document_id !== id));
      toast.success('Document supprimé');
    } catch { 
      toast.error('Erreur'); 
    }
  };
  
  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start gap-4">
        <div>
          <h2 className="font-orbitron text-2xl md:text-3xl font-bold text-white mb-2">Mes Documents</h2>
          <p className="text-gray-400 font-mono text-sm">// STOCKAGE_30_JOURS</p>
        </div>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button 
              variant="destructive" 
              className="bg-red-600/20 border border-red-600 text-red-400 w-full md:w-auto py-4" 
              data-testid="panic-room-btn"
            >
              <AlertTriangle className="w-4 h-4 mr-2" />PANIC ROOM
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent className="bg-void border border-red-600">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-red-400 font-orbitron">⚠️ SUPPRESSION TOTALE</AlertDialogTitle>
              <AlertDialogDescription className="text-gray-400">
                Tous vos documents seront supprimés définitivement.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="bg-transparent border border-gray-600 text-gray-400">Annuler</AlertDialogCancel>
              <AlertDialogAction onClick={onPanicRoom} className="bg-red-600 text-white">SUPPRIMER TOUT</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
      
      {/* Drag & Drop Zone */}
      <div 
        className={`glass-card p-6 md:p-8 border-2 border-dashed transition-all duration-300 cursor-pointer ${
          isDragging ? 'border-cyan-400 bg-cyan-400/10 scale-[1.02]' : 'border-cyan-400/30 hover:border-cyan-400'
        }`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => fileInputRef.current?.click()}
        data-testid="upload-dropzone"
      >
        <input 
          ref={fileInputRef}
          type="file" 
          className="hidden" 
          onChange={handleUpload} 
          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" 
          data-testid="file-input" 
        />
        <div className="flex flex-col items-center justify-center py-4">
          {uploading ? (
            <Loader2 className="w-12 h-12 text-cyan-400 animate-spin mb-4" />
          ) : isDragging ? (
            <Download className="w-12 h-12 text-cyan-400 animate-bounce mb-4" />
          ) : (
            <Upload className="w-12 h-12 text-cyan-400 mb-4" />
          )}
          <p className="text-white font-medium mb-2 text-center">
            {uploading ? 'Upload en cours...' : isDragging ? 'Déposez votre fichier ici' : 'Cliquez ou glissez pour uploader'}
          </p>
          <p className="text-gray-500 text-sm">PDF, JPG, PNG, DOC • Max 10MB</p>
        </div>
      </div>
      
      <div className="space-y-3">
        {documents.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>Aucun document</p>
          </div>
        ) : documents.map((doc) => (
          <div key={doc.document_id} className="glass-card p-4 border border-gray-800 flex items-center justify-between gap-4">
            <div className="flex items-center gap-4 flex-1 min-w-0">
              <div className="w-10 h-10 bg-cyan-400/10 flex items-center justify-center flex-shrink-0">
                <FileText className="w-5 h-5 text-cyan-400" />
              </div>
              <div className="min-w-0">
                <p className="text-white font-medium truncate">{doc.filename}</p>
                <p className="text-gray-500 text-xs font-mono">{new Date(doc.upload_date).toLocaleDateString('fr-FR')}</p>
              </div>
            </div>
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => handleDelete(doc.document_id)} 
              className="text-red-400 hover:bg-red-400/10 flex-shrink-0 w-12 h-12" 
              data-testid={`delete-doc-${doc.document_id}`}
            >
              <Trash2 className="w-5 h-5" />
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
};

export { DocumentsTab };
